import {
  ChargerStatusBreakdown,
  DashboardOverviewResponse,
  DashboardOverviewWebsocketMessage,
  DashboardStats,
  EnergyCostResponse,
  VehicleOverviewResponse,
  VehicleStateOfChargeSummaryResponse,
} from '@core/api/multi-site-dashboard/multi-site-dashboard.models';
import { VehicleOperationalStatus } from '@core/api/single-site/single-site.enums';
import { ChargerOperationalStatus } from '@core/api/sites/sites.enums';

import { mapVehicleOperationalStatusApiModelEnumToStoreModelEnum } from '@domain/single-site/single-site-enums.mappings';
import { VehicleOperationalStatusStoreModelEnum } from '@domain/single-site/single-site.enums';
import { mapChargerOperationalStatusApiModelEnumToStoreModelEnum } from '@domain/sites/sites-enums.mappings';
import { ChargerOperationalStatusStoreModelEnum } from '@domain/sites/sites.enums';
import { mapWeatherIconApiModelToStoreModel } from '@domain/weather/weather-enums.mappings';

import { wToKw } from '@shared/utils/w-to-kw';

import {
  ChargerStatusBreakdownStoreModel,
  DashboardOverviewStoreModel,
  DashboardStatsStoreModel,
  EnergyCostStoreModel,
  VehicleOverviewStoreModel,
  VehicleStateOfChargeSummaryResponseStoreModel,
} from './multi-site-dashboard.models';

const mapVehicleStatusMapApiModelToStoreModel = (
  statusData: Partial<Record<VehicleOperationalStatus, number>>
): Partial<Record<VehicleOperationalStatusStoreModelEnum, number>> => {
  const result: Partial<Record<VehicleOperationalStatusStoreModelEnum, number>> = {};
  Object.entries(statusData).forEach(([status, count]) => {
    result[mapVehicleOperationalStatusApiModelEnumToStoreModelEnum(status as VehicleOperationalStatus)] = count;
  });

  return result;
};

const mapChargerStatusMapApiModelToStoreModel = (
  chargerOverview: Partial<Record<ChargerOperationalStatus, ChargerStatusBreakdown>>
): Partial<Record<ChargerOperationalStatusStoreModelEnum, ChargerStatusBreakdownStoreModel>> => {
  const result: Partial<Record<ChargerOperationalStatusStoreModelEnum, ChargerStatusBreakdownStoreModel>> = {};
  Object.entries(chargerOverview).forEach(([status, breakdown]) => {
    result[mapChargerOperationalStatusApiModelEnumToStoreModelEnum(status as ChargerOperationalStatus)] = {
      count: breakdown.count,
      siteCount: breakdown.siteCount,
    };
  });

  return result;
};

const mapVehicleOverviewApiModelToStoreModel = (vehicleOverview: VehicleOverviewResponse): VehicleOverviewStoreModel => ({
  statusData: mapVehicleStatusMapApiModelToStoreModel(vehicleOverview.statusData),
  siteId: vehicleOverview.siteId,
  depotName: vehicleOverview.depotName,
});

export const mapDashboardOverviewApiModelToStoreModel = (response: DashboardOverviewResponse): DashboardOverviewStoreModel => ({
  fleetOverview: mapVehicleStatusMapApiModelToStoreModel(response.fleetOverview),
  fleetPerSite: response.fleetPerSite.map((fleet) => mapVehicleOverviewApiModelToStoreModel(fleet)),
  chargerOverview: mapChargerStatusMapApiModelToStoreModel(response.chargerOverview),
});

export const mapDashboardOverviewWebsocketMessageApiModelToStoreModel = (
  message: DashboardOverviewWebsocketMessage
): DashboardOverviewStoreModel => mapDashboardOverviewApiModelToStoreModel(message);

export const mapDashboardStatsApiModelToStoreModel = (response: DashboardStats): DashboardStatsStoreModel => ({
  avgArrivalSoc: response.avgArrivalSoc,
  minDepartureSoc: response.minDepartureSoc,
  totalEnergyUsedKwh: wToKw(response.totalEnergyUsed),
  totalEnergyCost: response.totalEnergyCost,
  totalEnergyCostCurrency: response.totalEnergyCostCurrency,
});

export const mapEnergyCostApiModelToStoreModel = (response: EnergyCostResponse): EnergyCostStoreModel => ({
  siteNames: response.siteNames,
  graphData: response.graphData.map((unit) => ({
    date: unit.date,
    cost: unit.cost,
    costCurrency: unit.costCurrency,
    weather: {
      temperatureCelsius: unit.weather.temperature,
      icon: unit.weather.icon ? mapWeatherIconApiModelToStoreModel(unit.weather.icon) : null,
    },
    siteData: unit.siteData.map((site) => ({
      siteId: site.siteId,
      energyKwh: wToKw(site.energy),
    })),
  })),
});

export const mapVehicleStateOfChargeSummaryApiModelToStoreModel = (
  response: VehicleStateOfChargeSummaryResponse
): VehicleStateOfChargeSummaryResponseStoreModel => ({
  vehicleStateOfChargeSummary: response.vehicleStateOfChargeSummary.map((summary) => ({
    siteId: summary.siteId,
    siteName: summary.siteName,
    chargingOutcome: summary.chargingOutcome.map((outcome) => ({
      date: outcome.date,
      fullyChargedCount: outcome.fullyChargedCount,
      notFullyChargedCount: outcome.notFullyChargedCount,
    })),
  })),
});
